import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api } from "@/lib/api";

/**
 * Slice M · Places map — MapLibre GL, Grab-partner init pattern.
 *
 * Style priority:
 *   1. `styleUrl` prop wins
 *   2. Grab Maps `urban-light-partner` when REACT_APP_GRAB_MAPS_KEY is set
 *   3. OpenFreeMap Liberty (free, no-key, worldwide)
 *   4. MapLibre demotiles fallback if the primary style fails
 *
 * Data layers (added on `load`):
 *   • `places-circles` — canonical `db.places` viewport-loaded, circle color
 *     data-driven by freshness (green <24 h · amber <7 d · grey older).
 *   • `route-line-layer` — optional GeoJSON route overlay in Grab emerald.
 *
 * Props:
 *   • center: [lat, lng]                default Mumbai
 *   • zoom, pitch, bearing              default 14 / 0 / 0
 *   • minZoom, maxZoom                  default 3 / 20
 *   • pois=false disables the /places/near layer (route-only mode)
 *   • routeGeoJson: LineString / Feature — updates live when the ref changes.
 */
const _OPENFREE_LIBERTY   = "https://tiles.openfreemap.org/styles/liberty";
const _MAPLIBRE_DEMO      = "https://demotiles.maplibre.org/style.json";
const _GRAB_STYLE_URL     = (name) => `https://maps.grab.com/api/v1/styles/${name}/descriptor`;
const _GRAB_DEFAULT_STYLE = "urban-light-partner";

const _PLACES_SRC   = "places-src";
const _PLACES_LAYER = "places-circles";
const _ROUTE_SRC    = "route-line";
const _ROUTE_LAYER  = "route-line-layer";


function _freshnessBucket(iso) {
  if (!iso) return "stale";
  const ageMs = Date.now() - new Date(iso).getTime();
  const day   = 24 * 3600 * 1000;
  if (ageMs < day)         return "fresh";     // green
  if (ageMs < 7 * day)     return "recent";    // amber
  return "stale";                              // grey
}
function _freshnessLabel(iso) {
  if (!iso) return "Unknown age";
  const ageMs = Date.now() - new Date(iso).getTime();
  const min   = Math.floor(ageMs / 60000);
  if (min < 60)  return `Verified ${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)   return `Verified ${hr}h ago`;
  const dy = Math.floor(hr / 24);
  return `Verified ${dy}d ago`;
}


export default function PlacesMap({
  center       = [19.076, 72.877],
  zoom         = 14,
  pitch        = 0,          // Grab recommends 45° for a tilted city view
  bearing      = 0,
  minZoom      = 3,
  maxZoom      = 20,
  maxBounds    = null,       // [[swLng, swLat], [neLng, neLat]] — restrict panning
  height       = "100%",
  pois         = true,
  routeGeoJson = null,
  markers      = null,       // [{key, lat, lng, label?, color?, pulse?}]
  followKey    = null,       // recenter the map when this marker's coords change
  styleUrl     = null,
  grabStyle    = _GRAB_DEFAULT_STYLE,
}) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const debounceRef  = useRef(null);
  const abortRef     = useRef(null);
  const popupRef     = useRef(null);
  const explicitMarkersRef = useRef([]);
  const [count, setCount]     = useState(0);
  const [loading, setLoading] = useState(false);

  const grabKey = (typeof process !== "undefined") && process.env?.REACT_APP_GRAB_MAPS_KEY;

  async function _resolveInitialStyle() {
    if (styleUrl) return styleUrl;
    if (grabKey) {
      try {
        const r = await fetch(_GRAB_STYLE_URL(grabStyle), {
          headers: { Authorization: `Bearer ${grabKey}` },
        });
        if (r.ok) return await r.json();
      } catch { /* fall through */ }
    }
    return _OPENFREE_LIBERTY;
  }

  // ─── Viewport /places fetch → circle layer ─────────────────────────────
  const fetchPlaces = async () => {
    const map = mapRef.current;
    if (!map || !pois) return;
    abortRef.current?.abort?.();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const c   = map.getCenter();
      const b   = map.getBounds();
      const ne  = b.getNorthEast(), sw = b.getSouthWest();
      const diag = _haversineMeters(ne.lat, ne.lng, sw.lat, sw.lng);
      const radius = Math.min(3000, Math.max(80, Math.round(diag / 2)));
      const r = await api.get("/places/near", {
        params: { lat: c.lat, lng: c.lng, radius },
        signal: ctrl.signal,
      });
      const items = r.data?.items || [];
      setCount(items.length);
      const fc = {
        type: "FeatureCollection",
        features: items.map(p => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [p.lng, p.lat] },
          properties: {
            place_id:   p.place_id,
            name:       p.name || "Unnamed",
            categories: (p.categories || []).join(" · "),
            freshness:  _freshnessBucket(p.updated_at || p.created_at),
            ageLabel:   _freshnessLabel(p.updated_at || p.created_at),
            distance_m: p.distance_m ?? null,
          },
        })),
      };
      const src = map.getSource(_PLACES_SRC);
      if (src) src.setData(fc);
    } catch (e) {
      if (e?.name !== "CanceledError") { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  // ─── Route overlay (add / update / clear on prop change) ────────────────
  const applyRouteLine = () => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (!routeGeoJson) {
      if (map.getLayer(_ROUTE_LAYER))  map.removeLayer(_ROUTE_LAYER);
      if (map.getSource(_ROUTE_SRC))   map.removeSource(_ROUTE_SRC);
      return;
    }
    const src = map.getSource(_ROUTE_SRC);
    if (src) {
      src.setData(routeGeoJson);
    } else {
      map.addSource(_ROUTE_SRC, { type: "geojson", data: routeGeoJson });
      map.addLayer({
        id:     _ROUTE_LAYER,
        type:   "line",
        source: _ROUTE_SRC,
        layout: { "line-cap": "round", "line-join": "round" },
        paint:  { "line-color": "#1FBF7F", "line-width": 6 },
      });
    }
  };

  // Slice O · explicit markers (pickup / driver / drop-off pins).
  const applyExplicitMarkers = () => {
    const map = mapRef.current;
    if (!map) return;
    explicitMarkersRef.current.forEach(m => m.remove());
    explicitMarkersRef.current = [];
    (markers || []).forEach(m => {
      if (!Number.isFinite(m.lat) || !Number.isFinite(m.lng)) return;
      const el = document.createElement("div");
      const color = m.color || "#1FBF7F";
      el.style.cssText = `
        display:inline-flex;align-items:center;justify-content:center;
        min-width:24px;height:24px;padding:0 6px;border-radius:12px;
        background:${color};color:#fff;font-weight:700;font-size:11px;
        border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);
        ${m.pulse ? "animation:f2n-pulse 1.4s infinite;" : ""}
      `;
      el.textContent = m.label || "";
      el.setAttribute("data-testid", `explicit-marker-${m.key || "x"}`);
      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([m.lng, m.lat])
        .addTo(map);
      explicitMarkersRef.current.push(marker);
    });
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;

    (async () => {
      const style = await _resolveInitialStyle();
      if (cancelled) return;

      const map = new maplibregl.Map({
        container:   containerRef.current,
        style,
        center:      [center[1], center[0]],   // MapLibre = [lng, lat]
        zoom,
        pitch,
        bearing,
        minZoom,
        maxZoom,
        ...(maxBounds ? { maxBounds } : {}),
        attributionControl: false,
        interactive: true,
      });
      map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
      map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");

      map.on("error", (ev) => {
        const msg = ev?.error?.message || "";
        if (msg.includes("tiles.openfreemap.org") || msg.includes("grab.com")) {
          try { map.setStyle(_MAPLIBRE_DEMO); } catch { /* ignore */ }
        }
      });

      map.on("load", () => {
        // Places circle layer — data-driven freshness colour.
        if (pois) {
          map.addSource(_PLACES_SRC, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
          });
          map.addLayer({
            id:     _PLACES_LAYER,
            type:   "circle",
            source: _PLACES_SRC,
            paint: {
              "circle-radius":       8,
              "circle-color": [
                "match", ["get", "freshness"],
                "fresh",  "#10b981",
                "recent", "#f59e0b",
                "#9ca3af",     // stale / default
              ],
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          });
          // Cursor + popup on click.
          map.on("mouseenter", _PLACES_LAYER, () => { map.getCanvas().style.cursor = "pointer"; });
          map.on("mouseleave", _PLACES_LAYER, () => { map.getCanvas().style.cursor = ""; });
          map.on("click", _PLACES_LAYER, (e) => {
            const f = e.features?.[0];
            if (!f) return;
            popupRef.current?.remove();
            const p = f.properties || {};
            const distTxt = p.distance_m == null || p.distance_m === "null" ? "" :
              (Number(p.distance_m) < 1000
                ? `<div style="color:#94a3b8;font-size:10px;margin-top:2px">${Math.round(Number(p.distance_m))} m away</div>`
                : `<div style="color:#94a3b8;font-size:10px;margin-top:2px">${(Number(p.distance_m) / 1000).toFixed(1)} km away</div>`);
            const catTxt = p.categories
              ? `<div style="color:#78716c;font-size:11px;margin-top:2px;text-transform:capitalize">${_escape(p.categories)}</div>`
              : "";
            const color = p.freshness === "fresh" ? "#10b981" : p.freshness === "recent" ? "#f59e0b" : "#9ca3af";
            popupRef.current = new maplibregl.Popup({ offset: 12, closeButton: false })
              .setLngLat(f.geometry.coordinates)
              .setHTML(`
                <div style="font-family:inherit;min-width:160px">
                  <div style="font-weight:700;color:#0c0a09;font-size:14px">${_escape(p.name)}</div>
                  ${catTxt}
                  <div style="display:inline-flex;align-items:center;gap:4px;color:#57534e;font-size:10px;margin-top:4px">
                    <span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${color}"></span>
                    ${_escape(p.ageLabel || "")}
                  </div>
                  ${distTxt}
                </div>`)
              .addTo(map);
          });
        }

        fetchPlaces();
        applyRouteLine();
        applyExplicitMarkers();
      });

      const onMove = () => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(fetchPlaces, 350);
      };
      map.on("moveend", onMove);
      map.on("zoomend", onMove);
      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
      popupRef.current?.remove?.();
      explicitMarkersRef.current.forEach(m => m.remove());
      explicitMarkersRef.current = [];
      const map = mapRef.current;
      if (map) { map.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.isStyleLoaded()) applyRouteLine();
    else map.once("load", applyRouteLine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeGeoJson]);

  // Slice O · re-render explicit markers when the prop changes and follow
  // the marker keyed by `followKey` if it moved (used for driver pings).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.isStyleLoaded()) applyExplicitMarkers();
    else map.once("load", applyExplicitMarkers);
    if (followKey && markers) {
      const followed = markers.find(m => m.key === followKey);
      if (followed && Number.isFinite(followed.lat)) {
        map.easeTo({ center: [followed.lng, followed.lat], duration: 700 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, followKey]);

  return (
    <div className="relative" style={{ height }} data-testid="places-map">
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
      {pois && loading && (
        <div className="absolute top-3 left-3 z-10 rounded-full bg-white/95 shadow px-3 py-1 text-[11px] font-bold text-stone-600 pointer-events-none" data-testid="places-map-loading">
          Loading places…
        </div>
      )}
      {pois && !loading && (
        <div className="absolute top-3 left-3 z-10 rounded-full bg-white/95 shadow px-3 py-1 text-[11px] text-stone-700 pointer-events-none" data-testid="places-map-count">
          {count} place{count === 1 ? "" : "s"} in view
        </div>
      )}
      {pois && (
        <div className="absolute bottom-14 left-3 z-10 rounded-xl bg-white/95 shadow px-2 py-1.5 text-[10px] text-stone-600 space-y-0.5 pointer-events-none" data-testid="places-map-legend">
          <div className="font-bold uppercase tracking-wide text-stone-400 mb-0.5">Freshness</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background:"#10b981" }}/> &lt; 24h</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background:"#f59e0b" }}/> &lt; 7d</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background:"#9ca3af" }}/> older</div>
        </div>
      )}
    </div>
  );
}


// ─── helpers ────────────────────────────────────────────────────────────────
function _haversineMeters(alat, alng, blat, blng) {
  const R = 6371000;
  const p1 = alat * Math.PI / 180;
  const p2 = blat * Math.PI / 180;
  const dphi = (blat - alat) * Math.PI / 180;
  const dlmb = (blng - alng) * Math.PI / 180;
  const x = Math.sin(dphi / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dlmb / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

function _escape(s) {
  return String(s ?? "").replace(/[&<>"']/g, ch => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  }[ch]));
}
